import { __decorate } from "tslib";
import { _isComputingDerivation, action, computed, isArrayLike, isObservableArray, isObservableObject, observable, runInAction, toJS, } from 'mobx';
import raf from 'raf';
import merge from 'lodash/merge';
import isObject from 'lodash/isObject';
import pick from 'lodash/pick';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import isNumber from 'lodash/isNumber';
import omit from 'lodash/omit';
import isPlainObject from 'lodash/isPlainObject';
import { isSame, warning } from '../utils';
import DataSet, { addDataSetField } from './DataSet';
import Field from './Field';
import { axiosConfigAdapter, checkFieldType, childrenInfoForDelete, findBindField, findBindFields, findBindTargetFields, generateData, generateJSONData, generateResponseData, getChainFieldName, getIf, getOrderFields, getRecordDynamicProps, getRecordValue, getSortedFields, getUniqueKeysAndPrimaryKey, isDirtyRecord, processIntlField, processToJSON, processValue, sortTree, useCascade, useDirtyField, useNormal, useSelected, findMinOrMaxFields, } from './utils';
import * as ObjectChainValue from '../object-chain-value';
import { BooleanValue, DataSetEvents, DataSetSelection, FieldIgnore, FieldType, RecordStatus } from './enum';
import { treeReduce } from '../tree-helper';
import { iteratorReduce, iteratorSome } from '../iterator-helper';
/**
 * 记录ID生成器
 */
const IDGen = (function* (start) {
    while (true) {
        yield ++start;
    }
})(1000);
export const EXPANDED_KEY = '__EXPANDED_KEY__'; // TODO:Symbol
const SELECTABLE_KEY = '__SELECTABLE_KEY__'; // TODO:Symbol
const DISABLED_KEY = '__DISABLED_KEY__'; // TODO:Symbol
const SELECT_KEY = '__SELECT_KEY__'; // TODO:Symbol
const UNSELECT_KEY = '__UNSELECT_KEY__'; // TODO:Symbol
const SELECT_TIMESTAMP = '__TIMESTAMP__'; // TODO:Symbol
function initRecordField(record, name, fieldProps) {
    const { dataSet } = record;
    return processIntlField(name, (langProps) => new Field(langProps, dataSet, record), fieldProps, dataSet);
}
function initRecordData(initData, dataSet) {
    const { childrenField } = dataSet.props;
    return observable.object(initData, childrenField && initData[childrenField] ? { [childrenField]: observable.ref } : undefined);
}
export default class Record {
    constructor(data = {}, dataSet = new DataSet(), status = RecordStatus.add) {
        runInAction(() => {
            this.dataSet = dataSet;
            this.ownerFields = observable.map();
            this.status = status;
            this.id = IDGen.next().value;
            const initData = isObservableObject(data) ? toJS(data) : data;
            this.data = initRecordData(initData, dataSet);
            this.processData(initData);
            raf(() => {
                dataSet.fields.forEach(field => field.processForLookupAndLovConfig(this));
            });
        });
    }
    get fields() {
        warning(false, 'If you only want to get some property from dataSet field or calculated by `dynamicProps` or `computedProps`. Don\'t to iterate fields from `record.fields`, please use `dataSet.fields` instead. And then use `dsField.get(someProp, record)` to get the property which you needed.');
        const fields = new Map();
        this.dataSet.fields.forEach((_, name) => {
            const tempField = this.getField(name);
            if (tempField) {
                fields.set(name, tempField);
            }
        });
        return observable.map(fields);
    }
    get pristineData() {
        const { dirtyData } = this;
        const data = toJS(this.data);
        if (dirtyData) {
            dirtyData.forEach((value, key) => ObjectChainValue.set(data, key, value));
        }
        return data;
    }
    set pristineData(data) {
        runInAction(() => {
            const { dirtyData, dataSet } = this;
            if (dirtyData) {
                const newData = {};
                if (dirtyData.size) {
                    dirtyData.forEach((_, key) => {
                        const item = ObjectChainValue.get(this.data, key);
                        ObjectChainValue.set(newData, key, item);
                        const newItem = ObjectChainValue.get(data, key);
                        if (isSame(item, newItem)) {
                            dirtyData.delete(key);
                        }
                        else {
                            dirtyData.set(key, newItem);
                        }
                    });
                }
                this.data = initRecordData(merge({}, data, newData), dataSet);
            }
            else {
                this.data = initRecordData(data, dataSet);
            }
        });
    }
    get selectedTimestamp() {
        return this.getState(SELECT_TIMESTAMP);
    }
    set selectedTimestamp(timestamp) {
        this.setState(SELECT_TIMESTAMP, timestamp);
    }
    get selectable() {
        const $selectable = this.getState(SELECTABLE_KEY);
        if ($selectable !== undefined) {
            return $selectable;
        }
        return getRecordDynamicProps(this, 'selectable', true);
    }
    set selectable(selectable) {
        this.setState(SELECTABLE_KEY, selectable);
    }
    get disabled() {
        const $disabled = this.getState(DISABLED_KEY);
        if ($disabled !== undefined) {
            return $disabled;
        }
        return getRecordDynamicProps(this, 'disabled', false);
    }
    set disabled(selectable) {
        this.setState(DISABLED_KEY, selectable);
    }
    get isDataSetInAllPageSelection() {
        return this.dataSet.isAllPageSelection;
    }
    get isSelected() {
        if (this.isDataSetInAllPageSelection) {
            return !this.getState(UNSELECT_KEY);
        }
        const $isSelected = this.getState(SELECT_KEY);
        if ($isSelected !== undefined) {
            return $isSelected;
        }
        return getRecordDynamicProps(this, 'defaultSelected', false);
    }
    set isSelected(isSelected) {
        if (isSelected !== this.isSelected) {
            const newState = {
                [SELECT_TIMESTAMP]: isSelected ? Date.now() : undefined,
            };
            if (this.isDataSetInAllPageSelection) {
                newState[UNSELECT_KEY] = !isSelected;
            }
            else {
                newState[SELECT_KEY] = isSelected;
            }
            this.setState(newState);
        }
    }
    get key() {
        if (!this.isNew) {
            const { primaryKey } = this.dataSet.props;
            if (primaryKey) {
                const key = this.get(primaryKey);
                if (isString(key) || isNumber(key)) {
                    return key;
                }
            }
        }
        return this.id;
    }
    get index() {
        return this.dataSet.indexOf(this);
    }
    get indexInParent() {
        const { parent } = this;
        if (parent && parent.children) {
            return parent.children.indexOf(this);
        }
        return this.dataSet.treeRecords.indexOf(this);
    }
    get isRemoved() {
        return this.status === RecordStatus.delete;
    }
    get isNew() {
        return this.status === RecordStatus.add;
    }
    get isSelectionIndeterminate() {
        if (this.dataSet.selection === DataSetSelection.multiple) {
            const { children } = this;
            if (children) {
                let checkedLength = 0;
                return (children.some(record => {
                    if (record.isSelectionIndeterminate) {
                        return true;
                    }
                    if (record.isSelected) {
                        checkedLength += 1;
                    }
                    return false;
                }) ||
                    (checkedLength > 0 && checkedLength !== children.length));
            }
        }
        return false;
    }
    get isIndeterminate() {
        const { dataSet } = this;
        const { checkField } = dataSet.props;
        if (checkField) {
            const field = dataSet.getField(checkField);
            const trueValue = field ? field.get(BooleanValue.trueValue, this) : true;
            const { children } = this;
            if (children) {
                let checkedLength = 0;
                return (children.some(record => {
                    if (record.isIndeterminate) {
                        return true;
                    }
                    if (record.get(checkField) === trueValue) {
                        checkedLength += 1;
                    }
                    return false;
                }) ||
                    (checkedLength > 0 && checkedLength !== children.length));
            }
        }
        return false;
    }
    get isExpanded() {
        const { dataSet } = this;
        const { expandField } = dataSet.props;
        if (expandField) {
            const expanded = this.get(expandField);
            const field = dataSet.getField(expandField);
            return expanded === (field ? field.get(BooleanValue.trueValue, this) : true);
        }
        const $expanded = this.getState(EXPANDED_KEY);
        if ($expanded !== undefined) {
            return $expanded;
        }
        return getRecordDynamicProps(this, 'defaultExpanded', false);
    }
    set isExpanded(expand) {
        const { dataSet } = this;
        const { expandField } = dataSet.props;
        if (expandField) {
            const field = dataSet.getField(expandField);
            this.set(expandField, field
                ? field.get(expand ? BooleanValue.trueValue : BooleanValue.falseValue, this)
                : expand);
        }
        else {
            this.setState(EXPANDED_KEY, expand);
        }
    }
    get previousRecord() {
        const { parent } = this;
        const children = parent ? parent.children : this.dataSet.treeData;
        if (children) {
            return children[children.indexOf(this) - 1];
        }
        return undefined;
    }
    get nextRecord() {
        const { parent } = this;
        const children = parent ? parent.children : this.dataSet.treeData;
        if (children) {
            return children[children.indexOf(this) + 1];
        }
        return undefined;
    }
    get records() {
        const { dataSet } = this;
        const { cascadeParent } = this;
        if (cascadeParent && !cascadeParent.isCurrent) {
            return cascadeParent.getCascadeRecordsIncludeDelete(dataSet.parentName) || [];
        }
        return dataSet.records;
    }
    get parents() {
        const { parent } = this;
        if (parent) {
            return [parent, ...parent.parents];
        }
        return [];
    }
    get path() {
        const path = [this, ...this.parents];
        path.reverse();
        return path;
    }
    get level() {
        const { parent } = this;
        if (parent) {
            return parent.level + 1;
        }
        return 0;
    }
    get dirty() {
        const { status } = this;
        if (status === RecordStatus.update) {
            return true;
        }
        const { dirtyData } = this;
        if (dirtyData && dirtyData.size > 0) {
            return true;
        }
        return Object.keys(this.dataSet.children).some(key => (this.getCascadeRecordsIncludeDelete(key) || []).some(isDirtyRecord));
    }
    get cascadeParent() {
        const { parent, parentName } = this.dataSet;
        if (parent && parentName) {
            return parent.cascadeRecords.find(record => (record.getCascadeRecordsIncludeDelete(parentName) || []).indexOf(this) !== -1);
        }
        return undefined;
    }
    processTreeLevel(props) {
        const { dataSet, value, fieldName } = props;
        const { parentField, idField, childrenField } = dataSet.props;
        if (!childrenField && parentField && idField) {
            if (fieldName === parentField) {
                const { parent } = this;
                if (parent) {
                    const oldChildren = parent.children;
                    if (oldChildren) {
                        const oldIndex = oldChildren.indexOf(this);
                        if (oldIndex !== -1) {
                            oldChildren.splice(oldIndex, 1);
                        }
                        if (!oldChildren.length) {
                            parent.children = undefined;
                        }
                    }
                }
                const newParent = dataSet.find(r => r.get(idField) === value);
                this.parent = newParent;
                if (newParent) {
                    const { children } = newParent;
                    if (children) {
                        children.push(this);
                        if (children.length > 1) {
                            const orderFields = getOrderFields(dataSet);
                            if (orderFields.length > 0) {
                                newParent.children = sortTree(children, orderFields);
                            }
                        }
                    }
                    else {
                        newParent.children = [this];
                    }
                }
            }
        }
    }
    /**
     * 转换成普通数据
     * 一般用于父级联数据源中的json类型字段
     * 禁止通过此方法获取某一个或几个字段值
     */
    toData(needIgnore, noCascade, isCascadeSelect, all = true) {
        if (_isComputingDerivation && _isComputingDerivation()) {
            warning(false, 'Please do not use the `record.toData` method to get the value during the rendering phase, please use `record.get` instead.');
        }
        const { status, dataSet } = this;
        const { dataToJSON, fields } = dataSet;
        const cascade = noCascade === undefined && dataToJSON ? useCascade(dataToJSON) : !noCascade;
        const normal = all || (dataToJSON && useNormal(dataToJSON));
        let dirty = status !== RecordStatus.sync;
        const childrenKeys = cascade ? Object.keys(dataSet.children) : undefined;
        const jsonFieldKeys = childrenKeys && iteratorReduce(fields.entries(), (fieldKeys, [key, field]) => {
            if (field.get('type', this) === FieldType.json && childrenKeys.some(childKey => key === childKey || childKey.startsWith(`${key}.`))) {
                fieldKeys.push(key);
            }
            return fieldKeys;
        }, []);
        const json = this.normalizeData(needIgnore, jsonFieldKeys);
        if (cascade && this.normalizeCascadeData(json, normal, isCascadeSelect)) {
            dirty = true;
        }
        if (jsonFieldKeys) {
            jsonFieldKeys.forEach(key => ObjectChainValue.set(json, key, JSON.stringify(ObjectChainValue.get(json, key)), fields, this));
        }
        return {
            ...json,
            __dirty: dirty,
        };
    }
    /**
     * 转换成用于提交的数据
     */
    toJSONData(noCascade, isCascadeSelect) {
        const { status, dataSet } = this;
        return {
            ...this.toData(true, noCascade, isCascadeSelect, false),
            __id: this.id,
            [dataSet.getConfig('statusKey')]: dataSet.getConfig('status')[status === RecordStatus.sync ? RecordStatus.update : status],
        };
    }
    validate(all, noCascade) {
        const { dataSet } = this;
        this.validating = true;
        const promises = [];
        if (all || this.status !== RecordStatus.sync) {
            dataSet.fields.forEach(field => promises.push(field.checkValidity(this)));
        }
        if (!noCascade) {
            const { children } = dataSet;
            const childrenKeys = Object.keys(children);
            if (childrenKeys.length) {
                const { dataSetSnapshot, isCurrent } = this;
                childrenKeys.forEach((key) => {
                    const snapshot = dataSetSnapshot && dataSetSnapshot[key];
                    const ds = children[key];
                    const child = isCurrent ? ds : snapshot && new DataSet(undefined, dataSet.context).restore(snapshot);
                    if (child) {
                        promises.push(child.validate());
                    }
                    else {
                        const { dataToJSON } = ds;
                        const cascade = noCascade === undefined && dataToJSON ? useCascade(dataToJSON) : !noCascade;
                        ((useSelected(dataToJSON) ? this.getCascadeSelectedRecords(key) : this.getCascadeRecords(key)) || []).forEach(record => promises.push(record.validate(false, !cascade)));
                    }
                });
            }
        }
        return Promise.all(promises)
            .then((results) => {
            const valid = results.every(result => result);
            if (!dataSet.validating) {
                dataSet.reportValidity({ record: this, errors: this.getValidationErrors(), valid });
            }
            delete this.validating;
            return valid;
        }).catch((error) => {
            delete this.validating;
            throw error;
        });
    }
    cancelReportValidity() {
        const { prepareForReport } = this;
        if (prepareForReport && prepareForReport.timeout) {
            window.clearTimeout(prepareForReport.timeout);
        }
    }
    reportValidity(result, fromField) {
        const { dataSet } = this;
        if (!dataSet.validating) {
            const prepareForReport = getIf(this, 'prepareForReport', {
                result: [],
                valid: true,
            });
            if (!result.valid) {
                prepareForReport.valid = false;
                prepareForReport.result.push(result);
            }
            if (prepareForReport.timeout) {
                window.clearTimeout(prepareForReport.timeout);
            }
            prepareForReport.timeout = window.setTimeout(() => {
                dataSet.reportValidity({ record: this, errors: prepareForReport.result, valid: prepareForReport.valid }, fromField);
                delete this.prepareForReport;
            }, 200);
        }
    }
    getValidationErrors() {
        const { validationErrors } = this;
        const results = [];
        if (validationErrors) {
            validationErrors.forEach((errors, fieldName) => {
                const field = this.ownerFields.get(fieldName) || this.dataSet.getField(fieldName);
                if (field && errors.length) {
                    results.push({
                        field,
                        errors,
                        valid: false,
                    });
                }
            });
        }
        return results;
    }
    addField(name, fieldProps) {
        const { dataSet, ownerFields } = this;
        if (!dataSet.getField(name)) {
            dataSet.addField(name, fieldProps);
        }
        const oldField = ownerFields.get(name);
        if (oldField) {
            return oldField.replace(fieldProps);
        }
        const [field, intlFields] = initRecordField(this, name, fieldProps);
        ownerFields.set(name, field);
        if (intlFields) {
            ownerFields.merge(intlFields);
        }
        return field;
    }
    getField(fieldName) {
        if (fieldName) {
            warning(false, 'If you only want to get some property from dataSet field or calculated by `dynamicProps` or `computedProps`. Don\'t to get field from `record.getField`, please use `dataSet.getField` instead. And then use `dsField.get(someProp, record)` to get the property which you needed.');
            {
                const field = this.ownerFields.get(fieldName);
                if (field) {
                    return field;
                }
            }
            const tempFields = getIf(this, 'tempFields', () => new Map());
            {
                const field = tempFields.get(fieldName);
                if (field) {
                    return field;
                }
            }
            const field = new Field({ name: fieldName }, this.dataSet, this);
            tempFields.set(fieldName, field);
            return field;
        }
    }
    getCascadeRecordsIncludeDelete(fieldName) {
        if (fieldName) {
            const childDataSet = this.dataSet.children[fieldName];
            if (childDataSet) {
                if (this.isCurrent) {
                    return childDataSet.records.slice();
                }
                const { dataSetSnapshot } = this;
                const snapshot = dataSetSnapshot && dataSetSnapshot[fieldName];
                if (snapshot) {
                    return snapshot.records;
                }
                const cascadeRecordsMap = getIf(this, 'cascadeRecordsMap', {});
                const cascadeRecords = cascadeRecordsMap[fieldName];
                if (cascadeRecords) {
                    return cascadeRecords;
                }
                const data = this.get(fieldName);
                const cascading = getIf(this, 'cascading', {});
                if (!cascading[fieldName] && isObservableArray(data)) {
                    cascading[fieldName] = true;
                    const records = childDataSet.processData(data, this.isNew ? RecordStatus.add : RecordStatus.sync);
                    delete cascading[fieldName];
                    cascadeRecordsMap[fieldName] = records;
                    return records;
                }
            }
        }
    }
    getCascadeRecords(fieldName) {
        const records = this.getCascadeRecordsIncludeDelete(fieldName);
        if (records) {
            return records.filter(r => !r.isRemoved);
        }
    }
    getCascadeSelectedRecordsIncludeDelete(fieldName) {
        const records = this.getCascadeRecordsIncludeDelete(fieldName);
        if (records) {
            return records.filter(r => r.isSelected);
        }
    }
    getCascadeSelectedRecords(fieldName) {
        const records = this.getCascadeRecordsIncludeDelete(fieldName);
        if (records) {
            return records.filter(r => !r.isRemoved && r.isSelected);
        }
    }
    get(fieldName) {
        return getRecordValue(this, (child, checkField) => child.get(checkField), fieldName);
    }
    set(item, value) {
        if (isString(item)) {
            const { dataSet } = this;
            const oldName = item;
            const fieldName = getChainFieldName(this, oldName);
            const field = dataSet.getField(oldName) || dataSet.getField(fieldName) || findBindField(oldName, fieldName, this) || addDataSetField(dataSet, oldName);
            checkFieldType(value, field, this);
            const oldValue = toJS(this.get(fieldName));
            const newValue = processValue(value, field, this);
            const newValueForCompare = processToJSON(newValue, field, this);
            const { fields } = dataSet;
            if (!isSame(processToJSON(oldValue, field, this), newValueForCompare)) {
                ObjectChainValue.set(this.data, fieldName, newValue, fields, this);
                const dirtyData = getIf(this, 'dirtyData', () => observable.map());
                if (!(dirtyData.has(fieldName))) {
                    dirtyData.set(fieldName, oldValue);
                    if (this.status === RecordStatus.sync) {
                        this.status = RecordStatus.update;
                        if (this.isCached && dataSet.cacheModifiedKeys) {
                            dataSet.cachedModified.push(this);
                        }
                    }
                }
                else if (isSame(processToJSON(dirtyData.get(fieldName), field, this), newValueForCompare)) {
                    dirtyData.delete(fieldName);
                    if (this.status === RecordStatus.update && dirtyData.size === 0) {
                        this.status = RecordStatus.sync;
                        if (this.isCached) {
                            if (!dataSet.cacheSelectionKeys || !this.isSelected) {
                                this.isCached = false;
                            }
                            const { cachedModified } = dataSet;
                            const cachedIndex = cachedModified.indexOf(this);
                            if (cachedIndex > -1) {
                                cachedModified.splice(cachedIndex, 1);
                            }
                        }
                    }
                }
                dataSet.fireEvent(DataSetEvents.update, {
                    dataSet,
                    record: this,
                    name: oldName,
                    value: newValue,
                    oldValue,
                });
                const { checkField, treeCheckStrictly } = dataSet.props;
                if (treeCheckStrictly !== true && checkField && fieldName === getChainFieldName(this, checkField)) {
                    const { children } = this;
                    if (children) {
                        children.forEach(record => record.set(fieldName, value));
                    }
                }
                this.processTreeLevel({
                    dataSet,
                    value: newValue,
                    fieldName: oldName,
                });
            }
            [
                field,
                ...findBindFields(field, fields, true, this),
                ...findBindTargetFields(field, fields, true, this),
                ...findMinOrMaxFields(field, fields, this),
            ].forEach((oneField) => (oneField.checkValidity(this)));
        }
        else if (isPlainObject(item)) {
            Object.keys(item).forEach(key => this.set(key, item[key]));
        }
        return this;
    }
    getPristineValue(fieldName) {
        if (fieldName) {
            const chainFieldName = getChainFieldName(this, fieldName);
            const { dirtyData } = this;
            if (dirtyData) {
                if (dirtyData.has(chainFieldName)) {
                    return dirtyData.get(chainFieldName);
                }
                let v;
                const has = iteratorSome(dirtyData.entries(), ([key, value]) => {
                    if (chainFieldName.startsWith(`${key}.`)) {
                        v = ObjectChainValue.get(value, chainFieldName.replace(`${key}.`, ''));
                        return true;
                    }
                    return false;
                });
                if (has) {
                    return v;
                }
            }
            return this.get(chainFieldName);
        }
    }
    init(item, value) {
        const { data, dirtyData } = this;
        if (isString(item)) {
            const { dataSet } = this;
            const oldName = item;
            const fieldName = getChainFieldName(this, oldName);
            const field = dataSet.getField(oldName) || dataSet.getField(fieldName) || findBindField(oldName, fieldName, this) || addDataSetField(dataSet, fieldName);
            const newValue = processValue(value, field, this);
            if (dirtyData) {
                dirtyData.delete(fieldName);
            }
            ObjectChainValue.set(data, fieldName, newValue, dataSet.fields, this);
            field.commit(this);
            this.processTreeLevel({
                dataSet,
                value: newValue,
                fieldName: oldName,
            });
        }
        else if (isPlainObject(item)) {
            Object.keys(item).forEach(key => this.init(key, item[key]));
        }
        return this;
    }
    clone() {
        const { dataSet } = this;
        const cloneData = this.toData();
        const { primaryKey } = dataSet.props;
        if (primaryKey) {
            delete cloneData[primaryKey];
        }
        return new Record(cloneData, dataSet);
    }
    ready() {
        return Promise.resolve(true);
        // return Promise.all([...this.fields.values()].map(field => field.ready()));
    }
    async tls(name) {
        const { dataSet } = this;
        const tlsKey = dataSet.getConfig('tlsKey');
        if (name) {
            const tlsData = this.get(tlsKey) || {};
            if (!(name in tlsData)) {
                const { axios, lang } = dataSet;
                const { primaryKey } = dataSet.props;
                const newConfig = axiosConfigAdapter('tls', dataSet, {}, primaryKey && { key: this.get(primaryKey) }, { name, record: this });
                if (newConfig.url && !this.isNew) {
                    const result = await axios(newConfig);
                    if (result) {
                        const dataKey = dataSet.getConfig('dataKey');
                        this.commitTls(generateResponseData(result, dataKey)[0], name);
                    }
                }
                else {
                    this.commitTls(iteratorReduce(dataSet.fields.entries(), (data, [key, field]) => {
                        if (field.get('type', this) === FieldType.intl) {
                            data[key] = {
                                [lang]: this.get(key),
                            };
                        }
                        return data;
                    }, {}), name);
                }
            }
        }
    }
    reset() {
        this.cancelReportValidity();
        const { status, dataSet, dirty, isRemoved, validationErrors } = this;
        let hasError = false;
        if (validationErrors && validationErrors.size > 0) {
            hasError = true;
            validationErrors.clear();
        }
        dataSet.fields.forEach(field => field.commit(this));
        if (status === RecordStatus.update || isRemoved) {
            this.status = RecordStatus.sync;
        }
        if (hasError || isRemoved || dirty) {
            this.data = initRecordData(toJS(this.pristineData), dataSet);
            this.dirtyData = undefined;
            this.memo = undefined;
            if (!dataSet.resetInBatch) {
                dataSet.fireEvent(DataSetEvents.reset, { records: [this], dataSet, record: this });
            }
        }
        return this;
    }
    save() {
        this.memo = toJS(this.data);
        return this;
    }
    restore() {
        const { memo } = this;
        if (memo) {
            this.set(memo);
            this.memo = undefined;
        }
        return this;
    }
    clear() {
        this.dataSet.fields.forEach((_, key) => this.set(key, null));
        return this;
    }
    commit(data, dataSet) {
        if (dataSet) {
            const { records } = this;
            if (this.isRemoved) {
                {
                    const index = records.indexOf(this);
                    if (index !== -1) {
                        if (dataSet.records === records) {
                            dataSet.totalCount -= 1;
                        }
                        records.splice(index, 1);
                    }
                }
                const { parent } = this;
                if (parent) {
                    const { children } = parent;
                    if (children) {
                        const index = children.indexOf(this);
                        if (index !== -1) {
                            children.splice(index, 1);
                        }
                        if (!children.length) {
                            parent.children = undefined;
                        }
                    }
                    this.parent = undefined;
                }
                return this;
            }
            if (this.isNew) {
                const index = records.indexOf(this);
                if (index !== -1 && dataSet.records === records) {
                    dataSet.totalCount += 1;
                }
            }
            if (data) {
                this.data = initRecordData(data, dataSet);
                this.processData(data, true);
                const { children } = dataSet;
                const keys = Object.keys(children);
                if (keys.length) {
                    const { isCurrent } = this;
                    const tmpDs = new DataSet(undefined, dataSet.context);
                    const dataSetSnapshot = getIf(this, 'dataSetSnapshot', {});
                    keys.forEach(key => {
                        const snapshot = dataSetSnapshot[key];
                        const ds = children[key];
                        const child = isCurrent
                            ? ds
                            : snapshot && tmpDs.restore(snapshot);
                        if (child) {
                            dataSetSnapshot[key] = child.commitData(data[key] || []).snapshot();
                        }
                        else {
                            const cascadeRecords = this.getCascadeRecordsIncludeDelete(key);
                            if (cascadeRecords) {
                                cascadeRecords.forEach(r => r.commit(omit(r.toData(), ['__dirty']), ds));
                            }
                        }
                    });
                }
            }
            dataSet.fields.forEach(field => field.commit(this));
        }
        this.dirtyData = undefined;
        this.status = RecordStatus.sync;
        return this;
    }
    setState(item, value) {
        const state = getIf(this, 'state', () => observable.map());
        if (isString(item)) {
            state.set(item, value);
        }
        else if (isPlainObject(item)) {
            state.merge(item);
        }
        return this;
    }
    getState(key) {
        const { state } = this;
        return state && state.get(key);
    }
    treeReduce(fn, initialValue) {
        return treeReduce([this], fn, initialValue);
    }
    commitTls(data = {}, name) {
        const { dataSet } = this;
        const tlsKey = dataSet.getConfig('tlsKey');
        if (!(name in data)) {
            data[name] = {};
        }
        Object.keys(data).forEach((key) => {
            const value = data[key];
            const field = dataSet.getField(key);
            if (field) {
                const transformResponse = field.get('transformResponse', this);
                if (transformResponse) {
                    const originValue = { ...value };
                    Object.keys(value).forEach((language) => {
                        value[language] = transformResponse(value[language], originValue);
                    });
                }
            }
            ObjectChainValue.set(this.data, key, value[dataSet.lang]);
            ObjectChainValue.set(this.data, `${tlsKey}.${key}`, value);
        });
    }
    processFieldValue(fieldName, field, fields, newData, data, needMerge) {
        let value = ObjectChainValue.get(newData, fieldName);
        const chainFieldName = getChainFieldName(this, fieldName);
        const transformResponse = field.get('transformResponse', this);
        if (chainFieldName !== fieldName) {
            const bindValue = ObjectChainValue.get(newData, chainFieldName);
            if (isNil(value) && !isNil(bindValue)) {
                value = bindValue;
            }
        }
        if (transformResponse) {
            value = transformResponse(value, data);
        }
        value = processValue(value, field, this, !needMerge && this.isNew);
        if (value === null) {
            value = undefined;
        }
        if (needMerge && isObject(value)) {
            const oldValue = ObjectChainValue.get(this.data, chainFieldName);
            if (isObject(oldValue)) {
                value = merge(oldValue, value);
            }
        }
        ObjectChainValue.set(newData, chainFieldName, value, fields, this);
        ObjectChainValue.set(this.data, chainFieldName, value, fields, this);
    }
    setValidationError(name, result) {
        const validationErrors = getIf(this, 'validationErrors', () => observable.map());
        const originalResult = validationErrors.get(name);
        if ((result && result.length) || (originalResult && originalResult.length)) {
            validationErrors.set(name, result);
        }
    }
    getValidationError(name) {
        const { validationErrors } = this;
        if (validationErrors) {
            return validationErrors.get(name);
        }
    }
    clearValidationError(name) {
        const { validationErrors } = this;
        if (validationErrors) {
            const errors = validationErrors.get(name);
            if (errors) {
                errors.some(error => {
                    if (error.ruleName === 'uniqueError') {
                        const { validationProps } = error;
                        if (validationProps) {
                            const { unique } = validationProps;
                            if (isString(unique)) {
                                this.dataSet.fields.forEach((field, fieldName) => {
                                    if (fieldName !== name &&
                                        field.get('unique', this) === unique &&
                                        !field.get('multiple', this) &&
                                        !field.get('range', this)) {
                                        this.clearValidationError(fieldName);
                                    }
                                });
                            }
                        }
                        return true;
                    }
                    return false;
                });
            }
            return validationErrors.delete(name);
        }
    }
    processData(data = {}, needMerge) {
        const newData = { ...data };
        const fields = getSortedFields(this.dataSet);
        fields.forEach((field, fieldName) => this.processFieldValue(fieldName, field, fields, newData, data, needMerge));
    }
    normalizeData(needIgnore, jsonFields) {
        const { dataSet } = this;
        const { fields, dataToJSON } = dataSet;
        const onlyDirtyField = needIgnore && dataToJSON ? useDirtyField(dataToJSON) : false;
        const neverKeys = onlyDirtyField ? getUniqueKeysAndPrimaryKey(dataSet) : [];
        const json = onlyDirtyField ? pick(this.data, neverKeys) : toJS(this.data);
        const fieldIgnore = onlyDirtyField ? FieldIgnore.clean : undefined;
        const objectFieldsList = [];
        const normalFields = [];
        const ignoreFieldNames = new Set();
        fields.forEach((field, key) => {
            if (field && (!jsonFields || !jsonFields.includes(key))) {
                const ignore = field.get('ignore', this) || fieldIgnore;
                if (needIgnore &&
                    !neverKeys.includes(key) &&
                    (ignore === FieldIgnore.always || (ignore === FieldIgnore.clean && !field.isDirty(this)))) {
                    ignoreFieldNames.add(key);
                }
                else {
                    const type = field.get('type', this);
                    if (type === FieldType.object) {
                        const level = key.split('.').length - 1;
                        objectFieldsList[level] = (objectFieldsList[level] || []).concat(field);
                    }
                    else {
                        normalFields.push(field);
                    }
                }
            }
        });
        [...objectFieldsList, normalFields].forEach(items => {
            if (items) {
                items.forEach(field => {
                    const { name } = field;
                    let value = ObjectChainValue.get(onlyDirtyField ? { ...this.data, ...json } : json, name);
                    const bind = field.get('bind', this);
                    const transformRequest = field.get('transformRequest', this);
                    if (bind) {
                        value = this.get(getChainFieldName(this, name));
                    }
                    const old = value;
                    value = processToJSON(value, field, this);
                    if (transformRequest) {
                        // compatible old logic
                        value = isString(field.get('multiple', this)) && isArrayLike(old) ?
                            transformRequest(value, this) :
                            processToJSON(transformRequest(old, this), field, this);
                    }
                    if (value !== undefined) {
                        ObjectChainValue.set(json, name, value, fields, this);
                    }
                    else {
                        ignoreFieldNames.add(name);
                    }
                });
            }
        });
        [...ignoreFieldNames].forEach(key => ObjectChainValue.remove(json, key));
        return json;
    }
    normalizeCascadeData(json, normal, isSelect) {
        const { dataSetSnapshot, dataSet, isRemoved } = this;
        let dirty = false;
        const { children, fields } = dataSet;
        if (isRemoved) {
            childrenInfoForDelete(json, children);
        }
        else {
            const keys = Object.keys(children);
            if (keys.length) {
                const { isCurrent } = this;
                keys.forEach(name => {
                    const snapshot = dataSetSnapshot && dataSetSnapshot[name];
                    const child = (!isCurrent && snapshot && new DataSet(undefined, dataSet.context).restore(snapshot)) || children[name];
                    if (child) {
                        const { dataToJSON } = child;
                        const records = this.getCascadeRecordsIncludeDelete(name);
                        const selected = isSelect || useSelected(dataToJSON) ? this.getCascadeSelectedRecordsIncludeDelete(name) : records;
                        const jsonArray = normal || useNormal(dataToJSON)
                            ? records && generateData(records)
                            : selected && generateJSONData(child, selected);
                        if (jsonArray) {
                            if (jsonArray.dirty) {
                                dirty = true;
                            }
                            ObjectChainValue.set(json, name, jsonArray.data, fields, this);
                        }
                    }
                });
            }
        }
        return dirty;
    }
}
__decorate([
    computed
], Record.prototype, "fields", null);
__decorate([
    observable
], Record.prototype, "ownerFields", void 0);
__decorate([
    observable
], Record.prototype, "data", void 0);
__decorate([
    observable
], Record.prototype, "dirtyData", void 0);
__decorate([
    observable
], Record.prototype, "validationErrors", void 0);
__decorate([
    observable
], Record.prototype, "lookupTokens", void 0);
__decorate([
    observable
], Record.prototype, "attachmentCaches", void 0);
__decorate([
    computed
], Record.prototype, "pristineData", null);
__decorate([
    observable
], Record.prototype, "status", void 0);
__decorate([
    computed
], Record.prototype, "isSelected", null);
__decorate([
    observable
], Record.prototype, "isCurrent", void 0);
__decorate([
    observable
], Record.prototype, "isCached", void 0);
__decorate([
    observable
], Record.prototype, "editing", void 0);
__decorate([
    observable
], Record.prototype, "pending", void 0);
__decorate([
    observable
], Record.prototype, "childrenLoaded", void 0);
__decorate([
    observable
], Record.prototype, "state", void 0);
__decorate([
    computed
], Record.prototype, "key", null);
__decorate([
    computed
], Record.prototype, "index", null);
__decorate([
    computed
], Record.prototype, "indexInParent", null);
__decorate([
    computed
], Record.prototype, "isSelectionIndeterminate", null);
__decorate([
    computed
], Record.prototype, "isIndeterminate", null);
__decorate([
    computed
], Record.prototype, "isExpanded", null);
__decorate([
    computed
], Record.prototype, "previousRecord", null);
__decorate([
    computed
], Record.prototype, "nextRecord", null);
__decorate([
    computed
], Record.prototype, "records", null);
__decorate([
    observable
], Record.prototype, "children", void 0);
__decorate([
    observable
], Record.prototype, "parent", void 0);
__decorate([
    computed
], Record.prototype, "parents", null);
__decorate([
    computed
], Record.prototype, "path", null);
__decorate([
    computed
], Record.prototype, "dirty", null);
__decorate([
    computed
], Record.prototype, "cascadeParent", null);
__decorate([
    action
], Record.prototype, "processTreeLevel", null);
__decorate([
    action
], Record.prototype, "addField", null);
__decorate([
    action
], Record.prototype, "set", null);
__decorate([
    action
], Record.prototype, "init", null);
__decorate([
    action
], Record.prototype, "tls", null);
__decorate([
    action
], Record.prototype, "reset", null);
__decorate([
    action
], Record.prototype, "save", null);
__decorate([
    action
], Record.prototype, "restore", null);
__decorate([
    action
], Record.prototype, "clear", null);
__decorate([
    action
], Record.prototype, "commit", null);
__decorate([
    action
], Record.prototype, "setState", null);
__decorate([
    action
], Record.prototype, "commitTls", null);
__decorate([
    action
], Record.prototype, "setValidationError", null);
__decorate([
    action
], Record.prototype, "clearValidationError", null);
